import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../api';
import DashboardLayout from '../../../Components/Dashboard/DashboardLayout';
import './AdminSuite.css';

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const formatCurrency = value => Number(value || 0).toLocaleString();

const AdminCohortReportsPage = () => {
  const [courses, setCourses] = useState([]);
  const [courseFilter, setCourseFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [report, setReport] = useState({ summary: [], totals: {} });

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    loadReport();
  }, [courseFilter, intakeFilter]);

  const loadCourses = async () => {
    const res = await api.get('/admin/courses');
    setCourses(res.data?.data || []);
  };

  const loadReport = async () => {
    const res = await api.get('/admin/reports/cohorts', {
      params: {
        ...(courseFilter === 'all' ? {} : { course_id: courseFilter }),
        ...(intakeFilter === 'all' ? {} : { course_intake_id: intakeFilter }),
      },
    });

    setReport(res.data || { summary: [], totals: {} });
  };

  const intakeOptions = useMemo(
    () =>
      courses
        .filter(course => courseFilter === 'all' || String(course.id) === String(courseFilter))
        .flatMap(course =>
          (course.intakes || []).map(intake => ({
            id: intake.id,
            name: `${course.title} - ${intake.name}`,
          }))
        ),
    [courses, courseFilter]
  );

  return (
    <DashboardLayout>
      <div className="container py-4 admin-suite">
        <div className="admin-suite-header admin-suite-header-title-row">
          <div>
            <h3 className="admin-suite-title">Cohort Reports</h3>
            <p className="admin-suite-subtitle">Track pipeline, occupancy and module demand by intake.</p>
          </div>
        </div>

        <div className="admin-suite-header admin-suite-header-filter-row">
          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end w-100">
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 220 }}
              value={courseFilter}
              onChange={event => {
                setCourseFilter(event.target.value);
                setIntakeFilter('all');
              }}
            >
              <option value="all">All courses</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </select>

            <select
              className="form-select form-select-sm"
              style={{ minWidth: 260 }}
              value={intakeFilter}
              onChange={event => setIntakeFilter(event.target.value)}
            >
              <option value="all">All intakes</option>
              {intakeOptions.map(intake => (
                <option key={intake.id} value={intake.id}>
                  {intake.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-md-3">
            <div className="card admin-card p-3">
              <div className="small text-muted">Applications</div>
              <div className="h4 mb-0">{report.totals?.applications || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card admin-card p-3">
              <div className="small text-muted">Approved</div>
              <div className="h4 mb-0">{report.totals?.approved || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card admin-card p-3">
              <div className="small text-muted">Pending</div>
              <div className="h4 mb-0">{report.totals?.pending || 0}</div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card admin-card p-3">
              <div className="small text-muted">Approved revenue</div>
              <div className="h4 mb-0">RWF {formatCurrency(report.totals?.approved_revenue)}</div>
            </div>
          </div>
        </div>

        <div className="card admin-card mt-3">
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr>
                  <th>Course / Intake</th>
                  <th>Dates</th>
                  <th>Capacity</th>
                  <th>Pipeline</th>
                  <th>Occupancy</th>
                  <th>Revenue</th>
                  <th>Top modules</th>
                </tr>
              </thead>
              <tbody>
                {report.summary?.map(row => (
                  <tr key={row.intake_id}>
                    <td>
                      <strong>{row.course_title}</strong>
                      <div className="small text-muted">{row.intake_name}</div>
                    </td>
                    <td>
                      {formatDate(row.start_date)} - {formatDate(row.end_date)}
                    </td>
                    <td>{row.capacity || '-'}</td>
                    <td>
                      <div className="small">Total: {row.total_applications}</div>
                      <div className="small text-warning-emphasis">Pending: {row.pending_applications}</div>
                      <div className="small text-success-emphasis">Approved: {row.approved_applications}</div>
                      <div className="small text-danger-emphasis">Rejected: {row.rejected_applications}</div>
                    </td>
                    <td>{row.occupancy_rate == null ? '-' : `${row.occupancy_rate}%`}</td>
                    <td>RWF {formatCurrency(row.approved_revenue)}</td>
                    <td>
                      {(row.module_popularity || []).slice(0, 3).map(module => (
                        <div key={module.module_id} className="small">
                          {module.module_name} ({module.selected_count})
                        </div>
                      ))}
                      {(!row.module_popularity || row.module_popularity.length === 0) && (
                        <span className="small text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!report.summary?.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No cohort data found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminCohortReportsPage;
